import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { X, Plus, Save, Loader2 } from "lucide-react";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import DrugSearchInput from "@/components/shared/DrugSearchInput";
import { DrugSearchResult } from "@/lib/api";
import {
    TABLET_DOSAGE_OPTIONS, WHEN_OPTIONS, DURATION_OPTIONS,
    FREQUENCY_OPTIONS, SEVERITY_OPTIONS, COMPLAINT_DURATION_OPTIONS,
} from "@/lib/constants";

// ─── Types ────────────────────────────────────────────────────────────────────

type TemplateType = "complaint" | "medicine" | "investigation" | "advice";
type TemplateCategory = "personal" | "clinic";

interface Medicine {
    brand_name: string;
    product: string;
    dosage: string;
    taken_when: string;
    duration: string;
    notes: string;
}

interface Complaint {
    complaint: string;
    frequency: string;
    severity: string;
    duration: string;
    date: string;
}

interface BuilderData {
    title?: string;
    description?: string;
    type?: TemplateType;
    category?: TemplateCategory;
    value?: any;
}

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: {
        title: string;
        description: string;
        type: TemplateType;
        category: TemplateCategory;
        value: any;
    }) => Promise<void>;
    initialData: BuilderData;
    loading: boolean;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface RowField {
    key: string;
    label: string;
    placeholder: string;
    options?: string[];
    /** Relative column width, mirrors FormTable's column `span` (out of ~12) */
    span?: number;
    /** Derive extra fields to update alongside this one (e.g. brand_name → product) */
    derive?: (value: string) => Record<string, string>;
    /** Render as a drug-database search box instead of a plain/autocomplete input */
    drugSearch?: boolean;
}

// Plain text fields are wrapped to match AutocompleteInput's "outlined" variant DOM exactly,
// so every field in a row — search box, autocomplete, plain input — renders at the same height.
const FIELD_WRAPPER_CLASS = "border border-border rounded-md bg-card focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/20 transition-colors px-2";
const PLAIN_INPUT_CLASS = "h-auto border-0 shadow-none bg-transparent rounded-none px-0 py-1.5 text-sm focus-visible:ring-0";

// Spans mirror MedicineTableColumns in FormTable.tsx (medicine 5 / dosage 2 / when 2 / duration 1 / notes 2)
const MEDICINE_FIELDS: RowField[] = [
    { key: "brand_name", label: "Medicine", placeholder: "Search medicine…", drugSearch: true, span: 5 },
    { key: "dosage",     label: "Dosage",   placeholder: "e.g. 1-0-1",   options: TABLET_DOSAGE_OPTIONS, span: 2 },
    { key: "taken_when", label: "Timing",   placeholder: "After food",   options: WHEN_OPTIONS,          span: 2 },
    { key: "duration",   label: "Duration", placeholder: "5 days",       options: DURATION_OPTIONS,      span: 1 },
    { key: "notes",      label: "Notes",    placeholder: "Notes",                                        span: 2 },
];

// Spans mirror ComplaintsTableColumns in FormTable.tsx (complaint 4 / frequency 2 / severity 2 / duration 2 / since 2)
const COMPLAINT_FIELDS: RowField[] = [
    { key: "complaint", label: "Complaint", placeholder: "e.g. Fever",                                       span: 4 },
    { key: "frequency", label: "Frequency", placeholder: "Continuous",  options: FREQUENCY_OPTIONS,          span: 2 },
    { key: "severity",  label: "Severity",  placeholder: "Mild",        options: SEVERITY_OPTIONS,           span: 2 },
    { key: "duration",  label: "Duration",  placeholder: "3 days",      options: COMPLAINT_DURATION_OPTIONS, span: 2 },
    { key: "date",      label: "Since",     placeholder: "Since today",                                      span: 2 },
];

function RowFieldHeader({ fields }: { fields: RowField[] }) {
    return (
        <div className="flex gap-2 mb-1 px-0.5">
            {fields.map(f => (
                <span
                    key={f.key}
                    style={{ flex: f.span ?? 1, minWidth: 0 }}
                    className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide truncate"
                >
                    {f.label}
                </span>
            ))}
            <span className="w-9 flex-shrink-0" />
        </div>
    );
}

function TemplateRow({
                          value, fields, onChange, onRemove,
                      }: {
    value: Record<string, string>;
    fields: RowField[];
    onChange: (v: Record<string, string>) => void;
    onRemove: () => void;
}) {
    const update = (key: string, val: string, derive?: RowField["derive"]) =>
        onChange({ ...value, [key]: val, ...(derive?.(val) || {}) });

    const selectDrug = (key: string, drug: DrugSearchResult) => onChange({
        ...value,
        [key]:       drug.brand_name,
        product:     drug.brand_name,
        dosage:      drug.custom_dosage     ?? drug.default_dosage     ?? value.dosage,
        taken_when:  drug.custom_taken_when ?? drug.default_taken_when ?? value.taken_when,
        duration:    drug.custom_duration   ?? drug.default_duration   ?? value.duration,
        notes:       drug.default_notes ?? value.notes,
    });

    return (
        <div className="flex gap-2 mb-2">
            {fields.map(f => {
                const style = { flex: f.span ?? 1, minWidth: 0 };
                if (f.drugSearch) return (
                    <div key={f.key} style={style} className={FIELD_WRAPPER_CLASS}>
                        <DrugSearchInput
                            value={value[f.key] || ""}
                            onChange={drug => selectDrug(f.key, drug)}
                            onCustomInput={v => update(f.key, v, vv => ({ product: vv }))}
                            placeholder={f.placeholder}
                            className="font-normal py-1.5 text-sm"
                        />
                    </div>
                );
                if (f.options) return (
                    <div key={f.key} style={style}>
                        <AutocompleteInput
                            value={value[f.key] || ""}
                            onChange={v => update(f.key, v, f.derive)}
                            options={f.options}
                            placeholder={f.placeholder}
                            variant="outlined"
                            inputSize="md"
                            showAllOnFocus
                            dropdownMinWidth={160}
                        />
                    </div>
                );
                return (
                    <div key={f.key} style={style} className={FIELD_WRAPPER_CLASS}>
                        <Input
                            value={value[f.key] || ""}
                            onChange={e => update(f.key, e.target.value, f.derive)}
                            placeholder={f.placeholder}
                            className={PLAIN_INPUT_CLASS}
                        />
                    </div>
                );
            })}
            <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
                <X className="w-4 h-4" />
            </Button>
        </div>
    );
}

function InvestigationRow({
                              value, idx, onChange, onRemove,
                          }: { value: string; idx: number; onChange: (v: string) => void; onRemove: () => void }) {
    return (
        <div className="flex gap-2 mb-2">
            <div className={cn("flex-1", FIELD_WRAPPER_CLASS)}>
                <Input
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder="Investigation name"
                    className={PLAIN_INPUT_CLASS}
                />
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
                <X className="w-4 h-4" />
            </Button>
        </div>
    );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY_MEDICINE: Medicine  = { brand_name: "", product: "", dosage: "", taken_when: "", duration: "", notes: "" };
const EMPTY_COMPLAINT: Complaint = { complaint: "", frequency: "", severity: "", duration: "", date: "" };

function initMedicines(data: BuilderData): Medicine[] {
    if (data.type !== "medicine") return [];
    const v = data.value;
    const arr = Array.isArray(v) ? v : [];
    if (arr.length === 0) return [EMPTY_MEDICINE];
    return arr.map((m: any) => ({
        brand_name: m.brand_name || m.product || "",
        product:    m.brand_name || m.product || "",
        dosage:     m.dosage     || "",
        taken_when: m.taken_when || m.when || "",
        duration:   m.duration   || "",
        notes:      m.notes      || "",
    }));
}

function initComplaints(data: BuilderData): Complaint[] {
    if (data.type !== "complaint") return [];
    const v = data.value;
    const arr = Array.isArray(v) ? v : [];
    if (arr.length === 0) return [EMPTY_COMPLAINT];
    return arr.map((c: any) => ({
        complaint: c.complaint || "",
        frequency: c.frequency || "",
        severity:  c.severity  || "",
        duration:  c.duration  || "",
        date:      c.date      || "",
    }));
}

function initInvestigations(data: BuilderData): string[] {
    if (data.type !== "investigation") return [];
    const v = data.value;
    const arr = Array.isArray(v) ? v : [];
    const mapped = arr.map((i: any) => {
        if (typeof i === "string") return i;
        if (typeof i === "object" && i.investigation) return i.investigation;
        return "";
    }).filter(Boolean);
    return mapped.length === 0 ? [""] : mapped;
}

function initAdvice(data: BuilderData): string {
    if (data.type !== "advice") return "";
    const v = data.value;
    if (typeof v === "string") return v;
    if (typeof v?.value === "string") return v.value;
    return "";
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TemplateBuilderModal({ open, onClose, onSave, initialData, loading }: Props) {
    const [type,        setType]        = useState<TemplateType>(initialData?.type || "medicine");
    const [title,       setTitle]       = useState(initialData?.title || "");
    const [description, setDescription] = useState(initialData?.description || "");
    const [category,    setCategory]    = useState<TemplateCategory>(initialData?.category || "personal");
    const [medicines,      setMedicines]      = useState<Medicine[]>(() => initMedicines(initialData));
    const [complaints,     setComplaints]     = useState<Complaint[]>(() => initComplaints(initialData));
    const [investigations, setInvestigations] = useState<string[]>(() => initInvestigations(initialData));
    const [advice,         setAdvice]         = useState(() => initAdvice(initialData));

    // Reset when modal opens
    useEffect(() => {
        if (!open) return;
        setType(initialData?.type        || "medicine");
        setTitle(initialData?.title      || "");
        setDescription(initialData?.description || "");
        setCategory(initialData?.category || "personal");
        setMedicines(initMedicines(initialData));
        setComplaints(initComplaints(initialData));
        setInvestigations(initInvestigations(initialData));
        setAdvice(initAdvice(initialData));
    }, [open, initialData]);

    // Clear content when user changes type — skip on first render
    const isFirstRender = React.useRef(true);
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        setMedicines(type === "medicine" ? [EMPTY_MEDICINE] : []);
        setComplaints(type === "complaint" ? [EMPTY_COMPLAINT] : []);
        setInvestigations(type === "investigation" ? [""] : []);
        setAdvice("");
    }, [type]);

    useEffect(() => {
        if (!open) isFirstRender.current = true;
    }, [open]);

    // ── Value assembly ─────────────────────────────────────────────────────────

    const getValue = useCallback(() => {
        if (type === "medicine")      return medicines;
        if (type === "complaint")     return complaints;
        if (type === "investigation") return investigations;
        if (type === "advice")        return { value: advice.trim() };
        return null;
    }, [type, medicines, complaints, investigations, advice]);

    // ── Validation ─────────────────────────────────────────────────────────────

    const isValid =
        title.trim() !== "" && (
            (type === "medicine"      && medicines.length > 0      && medicines.every(m => (m.brand_name || m.product).trim())) ||
            (type === "complaint"     && complaints.length > 0     && complaints.every(c => c.complaint.trim())) ||
            (type === "investigation" && investigations.length > 0 && investigations.every(i => i.trim())) ||
            (type === "advice"        && advice.trim() !== "")
        );

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <Dialog open={open} onOpenChange={() => !loading && onClose()}>
            <DialogContent className="max-w-4xl flex flex-col max-h-[88vh] p-0 gap-0">

                <DialogHeader className="px-6 py-4 border-b border-border flex-shrink-0">
                    <DialogTitle className="text-base font-semibold">
                        {initialData?.title ? "Edit Template" : "New Template"}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">

                    {/* Name, Type, Visibility — kept compact; the row data below is the main event */}
                    <div className="flex gap-3">
                        <div className="flex-1 space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Template Name <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                placeholder="e.g. Fever medicines"
                                disabled={loading}
                                className="bg-card"
                            />
                        </div>
                        <div className="w-40 space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Type <span className="text-destructive">*</span>
                            </Label>
                            <Select
                                value={type}
                                onValueChange={v => setType(v as TemplateType)}
                                disabled={loading || !!initialData?.title}
                            >
                                <SelectTrigger className="bg-card"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="medicine">Medicine</SelectItem>
                                    <SelectItem value="complaint">Complaint</SelectItem>
                                    <SelectItem value="investigation">Investigation</SelectItem>
                                    <SelectItem value="advice">Advice</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Visibility
                            </Label>
                            <div className="flex h-9 rounded-md border border-border overflow-hidden bg-card">
                                {(["personal", "clinic"] as TemplateCategory[]).map(c => (
                                    <button
                                        key={c}
                                        type="button"
                                        disabled={loading}
                                        onClick={() => setCategory(c)}
                                        className={cn(
                                            "px-3 text-sm font-medium capitalize transition-colors",
                                            c === "clinic" && "border-l border-border",
                                            category === c
                                                ? "bg-primary/10 text-primary"
                                                : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                        )}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Description — secondary, kept low-key */}
                    <div className="space-y-1">
                        <Label className="text-[11px] font-normal text-muted-foreground">
                            Description <span className="text-muted-foreground/60">(optional)</span>
                        </Label>
                        <Input
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="When to use this template..."
                            disabled={loading}
                            className="h-8 text-sm bg-card"
                        />
                    </div>

                    {/* Medicine rows */}
                    {type === "medicine" && (
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Medicines <span className="text-destructive">*</span>
                            </Label>
                            <RowFieldHeader fields={MEDICINE_FIELDS} />
                            {medicines.map((med, idx) => (
                                <TemplateRow
                                    key={idx} value={med as unknown as Record<string, string>} fields={MEDICINE_FIELDS}
                                    onChange={v => setMedicines(medicines.map((mm, i) => i === idx ? { ...mm, ...v } : mm))}
                                    onRemove={() => setMedicines(medicines.filter((_, i) => i !== idx))}
                                />
                            ))}
                            <Button type="button" variant="outline" size="sm"
                                    onClick={() => setMedicines([...medicines, EMPTY_MEDICINE])}>
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Medicine
                            </Button>
                        </div>
                    )}

                    {/* Complaint rows */}
                    {type === "complaint" && (
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Complaints <span className="text-destructive">*</span>
                            </Label>
                            <RowFieldHeader fields={COMPLAINT_FIELDS} />
                            {complaints.map((row, idx) => (
                                <TemplateRow
                                    key={idx} value={row as unknown as Record<string, string>} fields={COMPLAINT_FIELDS}
                                    onChange={v => setComplaints(complaints.map((rr, i) => i === idx ? { ...rr, ...v } : rr))}
                                    onRemove={() => setComplaints(complaints.filter((_, i) => i !== idx))}
                                />
                            ))}
                            <Button type="button" variant="outline" size="sm"
                                    onClick={() => setComplaints([...complaints, EMPTY_COMPLAINT])}>
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Complaint
                            </Button>
                        </div>
                    )}

                    {/* Investigation rows */}
                    {type === "investigation" && (
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Investigations <span className="text-destructive">*</span>
                            </Label>
                            {investigations.map((inv, idx) => (
                                <InvestigationRow
                                    key={idx} idx={idx} value={inv}
                                    onChange={v => setInvestigations(investigations.map((ii, i) => i === idx ? v : ii))}
                                    onRemove={() => setInvestigations(investigations.filter((_, i) => i !== idx))}
                                />
                            ))}
                            <Button type="button" variant="outline" size="sm"
                                    onClick={() => setInvestigations([...investigations, ""])}>
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Investigation
                            </Button>
                        </div>
                    )}

                    {/* Advice textarea */}
                    {type === "advice" && (
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Content <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                rows={6}
                                value={advice}
                                onChange={e => setAdvice(e.target.value)}
                                placeholder="Enter advice content..."
                                disabled={loading}
                                className="bg-card"
                            />
                        </div>
                    )}

                    {/* Preview — only meaningful for free-form text where wrapping/formatting matters */}
                    {type === "advice" && (
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Preview
                            </Label>
                            <div className="bg-muted/50 border border-border rounded-md p-3 min-h-[48px] text-sm whitespace-pre-wrap">
                                {advice || <span className="text-muted-foreground">Nothing to preview</span>}
                            </div>
                        </div>
                    )}

                </div>

                <DialogFooter className="px-6 py-4 border-t border-border flex-shrink-0">
                    <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={() => onSave({ title: title.trim(), description: description.trim(), type, category, value: getValue() })}
                        disabled={!isValid || loading}
                    >
                        {loading
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                            : <><Save className="w-4 h-4 mr-2" />Save Template</>
                        }
                    </Button>
                </DialogFooter>

            </DialogContent>
        </Dialog>
    );
}